import { useEffect, useRef, useState } from 'react';
import {
  apiClient,
  type ProjectDetail,
  type TaskListRow,
  type TaskStatus,
  type TaskPriority,
  type MemberRow,
  ApiError,
} from '@/lib/api/client';
import { ProjectSettingsDrawer } from './ProjectSettingsDrawer';
import { TaskDrawer } from './TaskDrawer';
import { ProjectChat } from './ProjectChat';
import { ProjectNotes } from './ProjectNotes';
import { ProjectLinks } from './ProjectLinks';
import { ProjectActivity } from './ProjectActivity';
import { ProjectInbox } from './ProjectInbox';
import { resolveProjectColor } from '@/lib/projectColors';
import { useUndo } from '@/lib/undo';
import { cn } from '@/lib/utils';
import { GlowCheck } from '@/components/GlowCheck';
import { ProjectAvatar } from '@/components/ProjectAvatar';
import { UserAvatar } from '@/components/UserAvatar';

interface Props {
  projectId: string;
  myUserId: string;
  onBack: () => void;
}

const STATUS_LABEL: Record<TaskStatus, string> = {
  todo: 'To do',
  in_progress: 'In progress',
  blocked: 'Blocked',
  done: 'Done',
  archived: 'Archived',
};

/** Per-user togglable columns on the task list. Persisted globally (not
 *  per-project) — most users want a single density preference everywhere.
 *  Tasks are grouped by status already, so a separate Status column would
 *  duplicate the section headers; priority pill is the per-row classifier. */
type TaskColumns = {
  priority: boolean;
  due: boolean;
  assignee: boolean;
  subtasks: boolean;
};
const DEFAULT_TASK_COLUMNS: TaskColumns = {
  priority: true,
  due: true,
  assignee: true,
  subtasks: true,
};
const TASK_COLUMNS_KEY = 'apetech-pm:task-columns';
function readTaskColumns(): TaskColumns {
  if (typeof window === 'undefined') return DEFAULT_TASK_COLUMNS;
  try {
    const raw = window.localStorage.getItem(TASK_COLUMNS_KEY);
    if (!raw) return DEFAULT_TASK_COLUMNS;
    const parsed = JSON.parse(raw) as Partial<TaskColumns>;
    return { ...DEFAULT_TASK_COLUMNS, ...parsed };
  } catch {
    return DEFAULT_TASK_COLUMNS;
  }
}

const STATUS_ORDER: TaskStatus[] = ['todo', 'in_progress', 'blocked', 'done', 'archived'];

const PRIORITY_LABEL: Record<TaskPriority, string> = {
  p0: 'P0 · Urgent',
  p1: 'P1 · High',
  p2: 'P2 · Normal',
  p3: 'P3 · Low',
};

const PRIORITY_COLOR: Record<TaskPriority, string> = {
  p0: 'oklch(0.62 0.22 25)', // urgent red
  p1: 'oklch(0.78 0.17 65)', // amber
  p2: 'oklch(0.72 0.14 235)', // info blue
  p3: 'oklch(0.55 0.01 265)', // muted gray-violet
};

const PRIORITY_SHORT: Record<TaskPriority, string> = {
  p0: 'P0',
  p1: 'P1',
  p2: 'P2',
  p3: 'P3',
};

type SubtaskLite = {
  task_id: string;
  title: string;
  status: TaskStatus;
  priority: TaskPriority;
  due_at: number | null;
};

function clipBody(s: string | null, max = 140): string {
  if (!s) return '';
  const normalized = s.replace(/\s+/g, ' ').trim();
  if (normalized.length <= max) return normalized;
  return normalized.slice(0, max - 1) + '…';
}

export function ProjectView({ projectId, myUserId, onBack }: Props) {
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [tasks, setTasks] = useState<TaskListRow[] | null>(null);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [quickTitle, setQuickTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const { pushUndo } = useUndo();
  const [filter, setFilter] = useState<'all' | 'mine' | 'open'>('all');
  const [columns, setColumns] = useState<TaskColumns>(() => readTaskColumns());
  const [columnsOpen, setColumnsOpen] = useState(false);
  /** Right column tab — Chat or Activity. Persisted per-user globally. */
  const [rightTab, setRightTab] = useState<'chat' | 'activity'>(() => {
    if (typeof window === 'undefined') return 'chat';
    return (window.localStorage.getItem('apetech-pm:project-right-tab') as 'chat' | 'activity') ?? 'chat';
  });
  useEffect(() => {
    window.localStorage.setItem('apetech-pm:project-right-tab', rightTab);
  }, [rightTab]);
  useEffect(() => {
    window.localStorage.setItem(TASK_COLUMNS_KEY, JSON.stringify(columns));
  }, [columns]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [subtaskCache, setSubtaskCache] = useState<Map<string, SubtaskLite[]>>(new Map());
  // Status-group collapse state — done + archived collapsed by default so the
  // visual weight goes to open work. Persisted per-user across sessions.
  const COLLAPSE_KEY = 'apetech-pm:project-status-collapsed';
  const [collapsed, setCollapsed] = useState<Set<TaskStatus>>(() => {
    try {
      const raw = localStorage.getItem(COLLAPSE_KEY);
      if (raw) return new Set(JSON.parse(raw) as TaskStatus[]);
    } catch {
      /* ignore */
    }
    return new Set<TaskStatus>(['done', 'archived']);
  });
  function toggleCollapsed(status: TaskStatus) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      try {
        localStorage.setItem(COLLAPSE_KEY, JSON.stringify(Array.from(next)));
      } catch {
        /* ignore */
      }
      return next;
    });
  }

  async function toggleExpand(task: TaskListRow) {
    const next = new Set(expanded);
    if (next.has(task.task_id)) {
      next.delete(task.task_id);
      setExpanded(next);
      return;
    }
    next.add(task.task_id);
    setExpanded(next);
    if (!subtaskCache.has(task.task_id)) {
      try {
        const detail = await apiClient.getTask(task.task_id);
        const lite: SubtaskLite[] = detail.subtasks.map((s) => ({
          task_id: s.task_id,
          title: s.title,
          status: s.status,
          priority: s.priority,
          due_at: s.due_at,
        }));
        setSubtaskCache((m) => new Map(m).set(task.task_id, lite));
      } catch {
        /* leave cache empty; render shows nothing */
      }
    }
  }

  async function createSubtask(parentTaskId: string, title: string) {
    const trimmed = title.trim();
    if (!trimmed) return;
    try {
      const created = await apiClient.createTask(projectId, {
        title: trimmed,
        parent_task_id: parentTaskId,
      });
      const lite: SubtaskLite = {
        task_id: created.task_id,
        title: created.title,
        status: created.status,
        priority: created.priority,
        due_at: created.due_at ?? null,
      };
      setSubtaskCache((m) => {
        const fresh = new Map(m);
        const existing = fresh.get(parentTaskId) ?? [];
        fresh.set(parentTaskId, [...existing, lite]);
        return fresh;
      });
      setTasks((rows) => {
        if (!rows) return rows;
        return rows.map((r) =>
          r.task_id === parentTaskId ? { ...r, subtask_total: r.subtask_total + 1 } : r,
        );
      });
      // Make sure the row stays expanded so the user sees the new subtask
      setExpanded((set) => {
        if (set.has(parentTaskId)) return set;
        const next = new Set(set);
        next.add(parentTaskId);
        return next;
      });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to add subtask');
    }
  }

  async function toggleSubtaskStatus(parentTaskId: string, subtaskId: string) {
    const list = subtaskCache.get(parentTaskId);
    if (!list) return;
    const sub = list.find((s) => s.task_id === subtaskId);
    if (!sub) return;
    const prevStatus: TaskStatus = sub.status;
    const nextStatus: TaskStatus = sub.status === 'done' ? 'todo' : 'done';

    /** Closure that applies one of the two statuses to local state +
     *  server. Reused by the forward path AND the undo/redo handlers. */
    const apply = async (status: TaskStatus, oldStatus: TaskStatus) => {
      setSubtaskCache((m) => {
        const fresh = new Map(m);
        const cur = fresh.get(parentTaskId);
        if (cur) fresh.set(parentTaskId, cur.map((s) => (s.task_id === subtaskId ? { ...s, status } : s)));
        return fresh;
      });
      setTasks((rows) => {
        if (!rows) return rows;
        return rows.map((r) => {
          if (r.task_id !== parentTaskId) return r;
          const delta = (status === 'done' ? 1 : 0) - (oldStatus === 'done' ? 1 : 0);
          return { ...r, subtask_done: Math.max(0, r.subtask_done + delta) };
        });
      });
      await apiClient.updateTask(subtaskId, { status });
    };

    try {
      await apply(nextStatus, prevStatus);
      const titlePreview = sub.title.length > 32 ? sub.title.slice(0, 32) + '…' : sub.title;
      pushUndo({
        label: nextStatus === 'done' ? `Marked done: ${titlePreview}` : `Reopened: ${titlePreview}`,
        undo: () => apply(prevStatus, nextStatus),
        redo: () => apply(nextStatus, prevStatus),
      });
    } catch (e) {
      refresh();
      setErr(e instanceof ApiError ? e.message : 'Update failed');
    }
  }

  async function refresh(silent = false) {
    try {
      const [p, t, m] = await Promise.all([
        apiClient.getProject(projectId),
        apiClient.listTasks(projectId, { parent_task_id: 'top' }),
        apiClient.listProjectMembers(projectId),
      ]);
      setProject(p);
      setTasks(t.tasks);
      setMembers(m.members);
      if (!silent) setErr(null);
    } catch (e) {
      if (!silent) setErr(e instanceof ApiError ? e.message : 'Failed to load');
    }
  }

  useEffect(() => {
    refresh();
    // 8s polling so a teammate's task add/edit shows up without a manual
    // refresh. Paused while tab is hidden + refetch on visibility return
    // (same pattern as ProjectChat). Silent mode so transient network
    // hiccups don't pop the error banner mid-collaboration.
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') refresh(true);
    }, 8000);
    function onVis() {
      if (document.visibilityState === 'visible') refresh(true);
    }
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  async function quickCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!quickTitle.trim()) return;
    setCreating(true);
    try {
      await apiClient.createTask(projectId, { title: quickTitle.trim() });
      setQuickTitle('');
      refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Create failed');
    } finally {
      setCreating(false);
    }
  }

  async function toggleStatus(task: TaskListRow) {
    const prev: TaskStatus = task.status;
    const next: TaskStatus = task.status === 'done' ? 'todo' : 'done';
    // Optimistic
    setTasks((rows) =>
      rows
        ? rows.map((r) => (r.task_id === task.task_id ? { ...r, status: next } : r))
        : rows,
    );
    try {
      await apiClient.updateTask(task.task_id, { status: next });
      // Register undo so Cmd+Z reverts the status change. Title is included
      // in the label so the toast tells the user exactly which task they
      // can undo (matters when toggling rapidly through a list).
      const titlePreview = task.title.length > 32 ? task.title.slice(0, 32) + '…' : task.title;
      const undoLabel = next === 'done' ? `Marked done: ${titlePreview}` : `Reopened: ${titlePreview}`;
      pushUndo({
        label: undoLabel,
        undo: async () => {
          setTasks((rows) =>
            rows ? rows.map((r) => (r.task_id === task.task_id ? { ...r, status: prev } : r)) : rows,
          );
          await apiClient.updateTask(task.task_id, { status: prev });
        },
        redo: async () => {
          setTasks((rows) =>
            rows ? rows.map((r) => (r.task_id === task.task_id ? { ...r, status: next } : r)) : rows,
          );
          await apiClient.updateTask(task.task_id, { status: next });
        },
      });
    } catch (e) {
      refresh();
      setErr(e instanceof ApiError ? e.message : 'Update failed');
    }
  }

  /** One-click self-assign on a row. Optimistic — bumps the row's
   *  assignee_email + assignee_user_id so the avatar appears instantly,
   *  then commits to the server. Undoable via the global Cmd+Z stack. */
  async function claimTask(task: TaskListRow) {
    const prevAssignee = task.assignee_user_id;
    const prevEmail = task.assignee_email;
    const meEmail = members.find((m) => m.user_id === myUserId)?.email ?? null;
    setTasks((rows) =>
      rows
        ? rows.map((r) =>
            r.task_id === task.task_id
              ? { ...r, assignee_user_id: myUserId, assignee_email: meEmail }
              : r,
          )
        : rows,
    );
    try {
      await apiClient.updateTask(task.task_id, { assignee_user_id: myUserId });
      const titlePreview = task.title.length > 32 ? task.title.slice(0, 32) + '…' : task.title;
      pushUndo({
        label: `Claimed: ${titlePreview}`,
        undo: async () => {
          setTasks((rows) =>
            rows
              ? rows.map((r) =>
                  r.task_id === task.task_id
                    ? { ...r, assignee_user_id: prevAssignee, assignee_email: prevEmail }
                    : r,
                )
              : rows,
          );
          await apiClient.updateTask(task.task_id, { assignee_user_id: prevAssignee });
        },
        redo: async () => {
          setTasks((rows) =>
            rows
              ? rows.map((r) =>
                  r.task_id === task.task_id
                    ? { ...r, assignee_user_id: myUserId, assignee_email: meEmail }
                    : r,
                )
              : rows,
          );
          await apiClient.updateTask(task.task_id, { assignee_user_id: myUserId });
        },
      });
    } catch (e) {
      refresh();
      setErr(e instanceof ApiError ? e.message : 'Claim failed');
    }
  }

  const filtered = (tasks ?? []).filter((t) => {
    if (filter === 'mine') return t.assignee_user_id === myUserId;
    if (filter === 'open') return t.status !== 'done' && t.status !== 'archived';
    return true;
  });

  const byStatus = STATUS_ORDER.map((s) => ({
    status: s,
    rows: filtered.filter((t) => t.status === s),
  })).filter((g) => g.rows.length > 0);

  // Resolve project's accent color and re-bind THREE CSS custom properties
  // on the ProjectView root: --glow-color (drives .section-glow + input
  // focus), --primary (every bg-primary / text-primary Tailwind class in
  // descendants — buttons, mention pills, done-task checkboxes), and
  // --ring (legacy focus rings). One override → whole project tints.
  const projectGlow = project ? resolveProjectColor(project.color) : null;
  const glowStyle = projectGlow
    ? ({
        ['--glow-color']: projectGlow,
        ['--primary']: projectGlow,
        ['--ring']: projectGlow,
      } as React.CSSProperties)
    : undefined;

  return (
    <main
      style={glowStyle}
      className="min-h-dvh animate-in fade-in-0 duration-150 ease-out bg-background text-foreground antialiased"
    >
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-card focus:px-4 focus:py-2 focus:text-sm">Skip to content</a>
      <header className="border-b border-border bg-card/50">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors duration-150 hover:border-ring/60 hover:text-foreground"
            >
              ← Projects
            </button>
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Project
              </div>
              <div className="truncate text-sm font-semibold">{project?.name ?? '…'}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Order: default first (serial-position effect). 'all' is the
                landing tab so it sits leftmost; convention says left = primary. */}
            {(['all', 'open', 'mine'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-xs font-medium capitalize transition-colors',
                  filter === f
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {f === 'mine' ? 'Mine' : f === 'open' ? 'Open' : 'All'}
              </button>
            ))}
            <ColumnsMenu
              open={columnsOpen}
              onOpenChange={setColumnsOpen}
              columns={columns}
              onChange={setColumns}
            />
            <button
              onClick={() => setSettingsOpen(true)}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:border-ring/60 hover:text-foreground"
            >
              Project Settings
            </button>
          </div>
        </div>
      </header>

      {/* 2-col split at lg: tasks on the left, project chat sticky on the
          right (380px). Below lg the chat collapses below the tasks. The
          left column owns the page header (h1, filter, quick-create) so the
          chat starts at the same top edge as the task list. */}
      <div className="mx-auto grid max-w-7xl gap-6 px-6 py-8 lg:grid-cols-[minmax(0,1fr)_380px]">
       <div className="min-w-0">
        {project && (
          <div className="flex items-start gap-4">
            {/* Project image / emoji / color initial — left of the name +
                slug line for at-a-glance project identity in the header. */}
            <ProjectAvatar
              name={project.name}
              image_key={project.image_key}
              emoji={project.image_key ? null : project.emoji}
              color={project.color}
              size="lg"
              shape="rounded"
            />
            <div className="min-w-0 flex-1 space-y-1">
              <InlineProjectName
                project={project}
                canEdit={project.role === 'owner' || project.role === 'admin'}
                onRenamed={refresh}
              />
              <p className="font-mono text-xs text-muted-foreground">
                /p/{project.slug} · in {project.ws_name} · your role: {project.role}
                {project.closed_at && (
                  <span className="ml-2 inline-flex items-center gap-1.5 rounded border border-muted-foreground/40 bg-muted/40 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    <span className="size-1.5 rounded-full bg-muted-foreground" aria-hidden />
                    Closed
                  </span>
                )}
              </p>
            </div>
          </div>
        )}
        {project?.closed_at && (
          <div
            role="status"
            className="mt-4 flex items-center gap-3 rounded-md border border-muted-foreground/30 bg-muted/30 px-4 py-2.5 text-sm text-muted-foreground"
          >
            <span aria-hidden>🔒</span>
            <span className="flex-1">
              This project is closed. Read-only — open settings to reopen.
            </span>
            {(project.role === 'owner' || project.role === 'admin') && (
              <button
                type="button"
                onClick={() => setSettingsOpen(true)}
                className="rounded-md border border-border bg-background px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-foreground hover:border-ring/60"
              >
                Reopen
              </button>
            )}
          </div>
        )}

        {err && (
          <div role="alert" className="mt-6 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
            {err}
          </div>
        )}

        {/* Quick-create — visually-hidden label + maxLength match task schema.
            Disabled when the project is closed (server also enforces). */}
        {!project?.closed_at && (
          <form onSubmit={quickCreate} className="mt-8 flex gap-2">
            <label htmlFor="quick-task-title" className="sr-only">
              Add a task
            </label>
            <input
              id="quick-task-title"
              value={quickTitle}
              onChange={(e) => setQuickTitle(e.target.value)}
              placeholder="Add a task, press Enter"
              disabled={creating}
              maxLength={200}
              className="section-glow block flex-1 rounded-md bg-card px-3 py-2.5 text-sm focus:outline-none disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={!quickTitle.trim() || creating}
              className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              Add
            </button>
          </form>
        )}

        {/* Project-scoped inbox — events routed here that haven't been
            converted to a task yet. Sits above tasks so triage happens
            before planning. Auto-hides when empty so it doesn't add
            clutter to a quiet project. */}
        <div className="mt-6">
          <ProjectInbox projectId={projectId} onConverted={() => refresh(true)} />
        </div>

        {/* Tasks grouped by status */}
        <div className="mt-8 space-y-8">
          {tasks === null ? (
            <div
              className="space-y-2 rounded-lg border border-border bg-card p-2"
              aria-busy="true"
              aria-label="Loading tasks"
            >
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-10 animate-pulse rounded bg-background/40" />
              ))}
            </div>
          ) : byStatus.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-card/40 px-5 py-12 text-center">
              <div className="text-base text-foreground">No tasks yet</div>
              <div className="mt-1 text-sm text-muted-foreground">
                Add one above, or wait for a routing rule to convert an event.
              </div>
            </div>
          ) : (
            /* Single flat list with collapsible mono section headers.
               No per-group card chrome — the row divide-y carries the
               visual rhythm. Triangle ▾/▸ on the left of each header
               reads as the disclosure affordance. */
            <div className="section-glow divide-y divide-border/40 overflow-hidden rounded-lg">
              {byStatus.map((g) => {
                const isCollapsed = collapsed.has(g.status);
                const headerId = `status-${g.status}-header`;
                const bodyId = `status-${g.status}-body`;
                return (
                  <section key={g.status} aria-labelledby={headerId}>
                    <button
                      type="button"
                      id={headerId}
                      onClick={() => toggleCollapsed(g.status)}
                      aria-expanded={!isCollapsed}
                      aria-controls={bodyId}
                      className="flex w-full items-center gap-2 bg-card/30 px-4 py-2 text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground transition-colors hover:bg-card/60 hover:text-foreground"
                    >
                      <span
                        aria-hidden
                        className="inline-block w-2 text-foreground/60 transition-transform"
                        style={{
                          transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                        }}
                      >
                        ▸
                      </span>
                      <span>{STATUS_LABEL[g.status]}</span>
                      <span className="text-muted-foreground/60">·</span>
                      <span className="tabular-nums">{g.rows.length}</span>
                      {isCollapsed && (
                        <span className="ml-2 text-muted-foreground/50 normal-case tracking-normal">
                          (show)
                        </span>
                      )}
                    </button>
                    {!isCollapsed && (
                      <ul
                        id={bodyId}
                        role="list"
                        className="divide-y divide-border/40 bg-card"
                      >
                        {g.rows.map((t) => (
                          <TaskRow
                            key={t.task_id}
                            task={t}
                            columns={columns}
                            members={members}
                            expanded={expanded.has(t.task_id)}
                            subtasks={subtaskCache.get(t.task_id)}
                            onToggleStatus={() => toggleStatus(t)}
                            onToggleExpand={() => toggleExpand(t)}
                            onOpenDrawer={() => setOpenTaskId(t.task_id)}
                            onToggleSubtask={(subId) => toggleSubtaskStatus(t.task_id, subId)}
                            onOpenSubtask={(subId) => setOpenTaskId(subId)}
                            onCreateSubtask={(title) => createSubtask(t.task_id, title)}
                            onClaim={() => claimTask(t)}
                          />
                        ))}
                      </ul>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </div>

        {/* Project Notes + Links — collaborative context below tasks.
            Any project member can add. Author + project owner/admin can delete. */}
        <div className="mt-8 space-y-4">
          <ProjectNotes projectId={projectId} myUserId={myUserId} members={members} />
          <ProjectLinks projectId={projectId} myUserId={myUserId} />
        </div>
       </div>

        {/* Right column — project chat. Height matches the tasks column
            via grid stretch (default align-items=stretch on grid). When
            tasks are short, chat is short; when tasks grow tall, chat
            grows with them and its internal message list scrolls. The
            flex column inside lets the chat header sit at top + sticky
            send input at bottom + scrollable messages in between. min-h
            floor so a brand-new project doesn't render a 60px chat. */}
        <aside className="section-glow flex min-h-[480px] flex-col overflow-hidden rounded-lg bg-card/30">
          {/* Right-column tabs: Chat (live conversation) ↔ Activity
              (synthesized history). Mutually exclusive — picking one hides
              the other to keep the column focused. State persisted per-user. */}
          <div role="tablist" aria-label="Right panel" className="flex items-center gap-0.5 border-b border-border/60 bg-card/30 px-2 py-1.5">
            {(['chat', 'activity'] as const).map((t) => {
              const active = rightTab === t;
              return (
                <button
                  key={t}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setRightTab(t)}
                  className={cn(
                    'rounded px-3 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors',
                    active
                      ? 'bg-primary/15 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                  )}
                >
                  {t === 'chat' ? 'Chat' : 'Activity'}
                </button>
              );
            })}
          </div>
          <div className="flex-1 min-h-0">
            {rightTab === 'chat' ? (
              <ProjectChat projectId={projectId} myUserId={myUserId} members={members} />
            ) : (
              <ProjectActivity projectId={projectId} myUserId={myUserId} members={members} />
            )}
          </div>
        </aside>
      </div>

      {openTaskId && (
        <TaskDrawer
          taskId={openTaskId}
          myUserId={myUserId}
          members={members}
          onClose={() => setOpenTaskId(null)}
          onChanged={() => refresh()}
        />
      )}
      {settingsOpen && (
        <ProjectSettingsDrawer
          projectId={projectId}
          myUserId={myUserId}
          onClose={() => setSettingsOpen(false)}
          onChanged={refresh}
          onDeleted={onBack}
        />
      )}
    </main>
  );
}

function dueShort(ms: number): string {
  const d = new Date(ms);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays < 0) return `${-diffDays}d overdue`;
  if (diffDays < 7) return `in ${diffDays}d`;
  return d.toLocaleDateString();
}

/**
 * Task row with surfaced detail (per `web-ux-expert` recognition-over-recall
 * principle): chevron-expand for subtasks, priority pill, inline body preview,
 * from-inbox provenance badge, due chip, assignee chip. Density "Daily App
 * Balanced" (Stitch directive) — mono for numbers, sans for prose, single
 * accent tone per priority. No side-stripe borders (Impeccable ban).
 */
function TaskRow({
  task,
  columns,
  members,
  expanded,
  subtasks,
  onToggleStatus,
  onToggleExpand,
  onOpenDrawer,
  onToggleSubtask,
  onOpenSubtask,
  onCreateSubtask,
  onClaim,
}: {
  task: TaskListRow;
  columns: TaskColumns;
  members: MemberRow[];
  expanded: boolean;
  subtasks: SubtaskLite[] | undefined;
  onToggleStatus: () => void;
  onToggleExpand: () => void;
  onOpenDrawer: () => void;
  onToggleSubtask: (subId: string) => void;
  onCreateSubtask: (title: string) => Promise<void>;
  onOpenSubtask: (subId: string) => void;
  onClaim: () => void;
}) {
  const isDone = task.status === 'done';
  const bodyPreview = clipBody(task.body_md);
  const hasSubtasks = task.subtask_total > 0;
  // API serializes `due_at` as an ISO STRING despite the client TS type
  // claiming `number | null`. Numeric comparison `string < number` coerces
  // the string to NaN and silently returns false — meaning every overdue
  // task evaluated as "not overdue" until 2026-05-24. new Date(x).getTime()
  // handles both representations correctly.
  const overdue =
    task.due_at !== null && new Date(task.due_at).getTime() < Date.now() && !isDone;

  return (
    <li>
      <div
        className={cn(
          'group flex items-start gap-3 px-4 py-3 transition-colors duration-100',
          'hover:bg-accent/30 focus-within:bg-accent/30',
          overdue && 'task-overdue',
        )}
      >
        <GlowCheck
          done={isDone}
          onClick={onToggleStatus}
          ariaLabel={isDone ? 'Mark task not done' : 'Mark task done'}
          size="md"
          className="mt-0.5"
        />

        {/* Chevron — always present so users can expand to add subtasks
            even when none exist yet. On HOVER-capable devices, hide while
            empty so the scan rhythm isn't cluttered. On touch (no hover),
            always show — chevron-on-hover is invisible to mobile users. */}
        <button
          onClick={onToggleExpand}
          aria-label={
            expanded
              ? 'Hide subtasks'
              : hasSubtasks
                ? `Show ${task.subtask_total} subtask${task.subtask_total === 1 ? '' : 's'}`
                : 'Add subtask'
          }
          aria-expanded={expanded}
          aria-controls={`subtasks-${task.task_id}`}
          className={cn(
            'mt-1 grid size-4 shrink-0 place-items-center rounded text-muted-foreground transition-all duration-150 hover:text-foreground',
            !hasSubtasks &&
              !expanded &&
              '[@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-60 focus-visible:opacity-100',
          )}
          style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
            <path
              d="M3 1 L7 5 L3 9"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Priority pill — only P0 (urgent) carries color. P1 gets a thin
            border in its tone, P2/P3 are mono on neutral. Was all four
            tinted — every task wore a hi-vis vest, so P0 lost its scream.
            Per PRODUCT.md "one alarm, not five." */}
        {columns.priority && <PriorityPill priority={task.priority} muted={isDone} />}

        {/* Title + body + meta-bottom */}
        <div className="min-w-0 flex-1">
          <button
            onClick={onOpenDrawer}
            className={cn(
              'block w-full text-left transition-colors duration-100',
              isDone ? 'text-muted-foreground line-through' : 'text-foreground hover:text-primary',
            )}
          >
            <span className="block truncate text-sm font-medium">{task.title}</span>
          </button>
          {bodyPreview && (
            <p className={cn(
              'mt-0.5 truncate text-xs text-muted-foreground',
              isDone && 'opacity-50',
            )}>
              {bodyPreview}
            </p>
          )}
          {/* Bottom meta strip — only shown when there's something to say.
              Provenance always shown when present; subtask count gated by
              the user's column-visibility preference. */}
          {(task.parent_event_id || (columns.subtasks && task.subtask_total > 0)) && (
            <div className="mt-1.5 flex items-center gap-2 font-mono text-[10px] text-muted-foreground">
              {task.parent_event_id && (
                <span className="inline-flex items-center gap-1 rounded bg-secondary/40 px-1.5 py-0.5">
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor" aria-hidden>
                    <circle cx="4" cy="4" r="2" />
                  </svg>
                  from inbox
                </span>
              )}
              {columns.subtasks && task.subtask_total > 0 && (
                <span
                  aria-label={`${task.subtask_done} of ${task.subtask_total} subtasks done`}
                >
                  {task.subtask_done}/{task.subtask_total} subtasks
                </span>
              )}
            </div>
          )}
        </div>

        {/* Right meta cluster — gated by user column preferences */}
        <div className="flex shrink-0 items-center gap-2 self-start pt-0.5 font-mono text-[10px] text-muted-foreground">
          {columns.due && task.due_at && (
            <span
              className={cn(
                'rounded px-1.5 py-0.5',
                overdue
                  ? 'bg-destructive/15 text-destructive'
                  : 'bg-accent/40',
              )}
              title={new Date(task.due_at).toLocaleString()}
            >
              {dueShort(task.due_at)}
            </span>
          )}
          {columns.assignee &&
            (task.assignee_email ? (
              (() => {
                // Resolve avatar via the project members array — the row
                // payload only carries email/user_id. Falls back to the
                // email-initial circle when no image is set.
                const m = members.find((x) => x.user_id === task.assignee_user_id);
                return (
                  <UserAvatar
                    email={task.assignee_email}
                    image_key={m?.image_key}
                    avatar_url={m?.avatar_url}
                    size={24}
                    title={`Assigned to ${task.assignee_email}`}
                  />
                );
              })()
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onClaim();
                }}
                title="Assign to me"
                aria-label="Assign to me"
                className="grid size-6 place-items-center rounded-full border border-dashed border-border text-[10px] text-muted-foreground/70 opacity-0 transition-colors group-hover:opacity-100 hover:border-primary hover:text-primary focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
              >
                +
              </button>
            ))}
        </div>
      </div>

      {/* Inline subtask list when expanded. Always rendered when expanded so
          users can add subtasks even when none exist yet. */}
      {expanded && (
        <div
          id={`subtasks-${task.task_id}`}
          role="group"
          aria-label={`Subtasks of ${task.title}`}
          className="ml-12 mr-4 space-y-0.5 border-l border-border/60 pl-3 pb-3 animate-in slide-in-from-top-1 fade-in-0 duration-150"
        >
          <ul className="space-y-0.5">
            {subtasks === undefined ? (
              <li className="py-2 font-mono text-[10px] text-muted-foreground" aria-busy="true">
                Loading subtasks…
              </li>
            ) : subtasks.length === 0 ? (
              <li className="py-2 font-mono text-[10px] text-muted-foreground/70">
                No subtasks yet — add one below.
              </li>
            ) : (
              subtasks.map((s) => {
                const subDone = s.status === 'done';
                const subOverdue =
                  s.due_at !== null && new Date(s.due_at).getTime() < Date.now() && !subDone;
                return (
                  <li
                    key={s.task_id}
                    className={cn(
                      'flex items-center gap-2.5 rounded px-2 py-1.5 transition-colors hover:bg-accent/20',
                      subOverdue && 'task-overdue',
                    )}
                  >
                    <GlowCheck
                      done={subDone}
                      onClick={() => onToggleSubtask(s.task_id)}
                      ariaLabel={subDone ? 'Mark subtask not done' : 'Mark subtask done'}
                      size="sm"
                    />
                    <button
                      onClick={() => onOpenSubtask(s.task_id)}
                      className={cn(
                        'min-w-0 flex-1 truncate text-left text-xs transition-colors',
                        subDone
                          ? 'text-muted-foreground line-through'
                          : 'text-foreground hover:text-primary',
                      )}
                    >
                      {s.title}
                    </button>
                    <PriorityPill priority={s.priority} size="sm" />
                  </li>
                );
              })
            )}
          </ul>
          <InlineSubtaskAdd onCreate={onCreateSubtask} />
        </div>
      )}
    </li>
  );
}

/** Inline "+ Add subtask" — appears at the bottom of an expanded TaskRow.
 *  Enter saves, then re-focuses so multiple subtasks can be entered in a row.
 *  Esc cancels and collapses to the placeholder. */
function InlineSubtaskAdd({ onCreate }: { onCreate: (title: string) => Promise<void> }) {
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);

  async function commit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await onCreate(trimmed);
      setTitle('');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={commit} className="mt-1 flex items-center gap-2.5 py-1.5">
      <span aria-hidden className="grid size-4 shrink-0 place-items-center text-muted-foreground/50">
        <svg viewBox="0 0 12 12" className="size-2.5" aria-hidden>
          <path d="M6 2 L6 10 M2 6 L10 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      </span>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setTitle('');
            (e.currentTarget as HTMLInputElement).blur();
          }
        }}
        disabled={saving}
        placeholder="Add subtask + Enter"
        maxLength={140}
        aria-label="New subtask"
        className="min-w-0 flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground/50 focus:outline-none disabled:opacity-60"
      />
    </form>
  );
}

/**
 * Click-to-edit project name. Single-click swaps the h1 into a sized input,
 * Enter commits, Esc reverts. Non-admins get a tooltip explaining why it's
 * read-only. PATCH /v1/projects/:id with { name } reuses the same endpoint
 * the Settings drawer uses.
 */
function InlineProjectName({
  project,
  canEdit,
  onRenamed,
}: {
  project: ProjectDetail;
  canEdit: boolean;
  onRenamed: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(project.name);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function start() {
    if (!canEdit) return;
    setDraft(project.name);
    setErr(null);
    setEditing(true);
  }
  function cancel() {
    setEditing(false);
    setErr(null);
    setDraft(project.name);
  }
  async function commit() {
    const next = draft.trim();
    if (!next || next === project.name) {
      cancel();
      return;
    }
    setSaving(true);
    try {
      await apiClient.updateProject(project.project_id, { name: next });
      setEditing(false);
      onRenamed();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to rename');
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <div className="space-y-1">
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') cancel();
          }}
          onBlur={commit}
          disabled={saving}
          maxLength={64}
          aria-label="Project name"
          className="block w-full rounded-md border border-ring/40 bg-card px-2 py-1 text-3xl font-semibold tracking-tight text-foreground focus:border-ring focus:outline-none md:text-4xl"
        />
        {err && (
          <p role="alert" className="font-mono text-[11px] text-destructive">
            {err}
          </p>
        )}
        {!err && (
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70">
            Enter to save · Esc to cancel
          </p>
        )}
      </div>
    );
  }

  // Heading + paired Rename button. Keep the h1 as a proper heading (no
  // role="button" collapse of semantics). The rename affordance is its own
  // focusable button, visible-on-hover for desktop and always-visible at
  // small viewports where there's no hover.
  return (
    <div className="group inline-flex items-center gap-2 text-balance">
      <h1 className="rounded-md px-2 py-0.5 -mx-2 text-3xl font-semibold tracking-tight md:text-4xl">
        {project.name}
      </h1>
      {canEdit && (
        <button
          type="button"
          onClick={start}
          aria-label={`Rename project ${project.name}`}
          className={cn(
            'rounded-md border border-border/40 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70',
            'transition-all duration-150 hover:border-ring/60 hover:text-foreground',
            'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
            // On touch (no hover), always show
            '[@media(hover:none)]:opacity-100',
          )}
        >
          Rename
        </button>
      )}
    </div>
  );
}

/** Stable hue (0-359) from a string. Used to disambiguate same-letter
 *  avatars (e.g. j.doe + john both show "J") with a per-user color tint. */
function stringToHue(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % 360;
}

/** Priority pill — P0 commits color (urgent), P1 borders only in tone,
 *  P2/P3 are mono-on-neutral. Solves the "every task screams" problem from
 *  uniform tinting. Used by the main TaskRow + the inline subtask list. */
function PriorityPill({
  priority,
  size = 'md',
  muted = false,
}: {
  priority: TaskPriority;
  size?: 'sm' | 'md';
  muted?: boolean;
}) {
  const dim =
    size === 'sm'
      ? 'h-[18px] min-w-[24px] text-[9px]'
      : 'mt-0.5 h-[22px] min-w-[28px] text-[10px]';
  const tone = PRIORITY_COLOR[priority];
  const style: React.CSSProperties =
    priority === 'p0'
      ? { background: tone, color: 'var(--background)' }
      : priority === 'p1'
        ? { borderColor: tone, color: tone }
        : { color: 'var(--muted-foreground)' };
  const klass = cn(
    'inline-grid shrink-0 place-items-center rounded font-mono font-semibold uppercase tracking-wider',
    dim,
    priority === 'p1' && 'border',
    priority !== 'p0' && priority !== 'p1' && 'bg-foreground/5',
    muted && 'opacity-50',
  );
  return (
    <span
      className={klass}
      style={style}
      title={PRIORITY_LABEL[priority]}
      aria-label={PRIORITY_LABEL[priority]}
    >
      {PRIORITY_SHORT[priority]}
    </span>
  );
}

/**
 * ColumnsMenu — popover button with per-column toggles. Click outside closes;
 * Escape closes. Status itself is the section header (rows are grouped by
 * status), so "Status" isn't togglable here — its column is the priority pill.
 */
function ColumnsMenu({
  open,
  onOpenChange,
  columns,
  onChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  columns: TaskColumns;
  onChange: (next: TaskColumns) => void;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) onOpenChange(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onOpenChange(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onOpenChange]);

  function toggle(k: keyof TaskColumns) {
    onChange({ ...columns, [k]: !columns[k] });
  }

  const items: Array<{ key: keyof TaskColumns; label: string }> = [
    { key: 'priority', label: 'Priority' },
    { key: 'due', label: 'Due date' },
    { key: 'assignee', label: 'Assignee' },
    { key: 'subtasks', label: 'Subtask count' },
  ];

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          'rounded-md border border-border px-3 py-1.5 text-xs font-medium transition-colors',
          open
            ? 'border-ring/60 bg-accent text-foreground'
            : 'text-muted-foreground hover:border-ring/60 hover:text-foreground',
        )}
        title="Show/hide task list columns"
      >
        Columns
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-2 w-56 rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-lg motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 motion-safe:duration-100"
          style={{ transformOrigin: 'top right' }}
        >
          <div className="px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Show columns
          </div>
          {items.map((item) => (
            <label
              key={item.key}
              className="flex cursor-pointer items-center justify-between gap-3 rounded px-2 py-1.5 text-sm text-foreground transition-colors hover:bg-accent"
            >
              <span>{item.label}</span>
              <input
                type="checkbox"
                checked={columns[item.key]}
                onChange={() => toggle(item.key)}
                className="size-4 accent-primary"
              />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
